import { User, Captions, LayoutGrid } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AvatarVoicePanel } from './AvatarVoicePanel';
import { LayoutsPanel } from './LayoutsPanel';
import { CaptionsPanel } from './CaptionsPanel';
import type { IRightSidebarProps, SidebarTab } from '@/@types/sceneEditorV2';

interface IIconMenuButtonProps {
  icon: LucideIcon;
  tab: SidebarTab;
  activeTab: SidebarTab;
  onTabChange: (tab: SidebarTab) => void;
  disabled?: boolean;
}

function IconMenuButton({
  icon: Icon,
  tab,
  activeTab,
  onTabChange,
  disabled = false,
}: IIconMenuButtonProps) {
  const isActive = activeTab === tab;

  return (
    <button
      onClick={() => {
        if (!disabled) onTabChange(tab);
      }}
      disabled={disabled}
      className={cn(
        'flex size-10 items-center justify-center rounded-md transition-colors',
        isActive &&
          'border border-primary/30 bg-primary/20 text-primary light:border-primary-foreground/30 light:bg-primary-foreground/12 light:text-primary-foreground',
        !isActive && !disabled && 'text-foreground-muted hover:text-foreground',
        disabled && 'cursor-not-allowed opacity-40'
      )}
      title={tab}
    >
      <Icon size={18} />
    </button>
  );
}

function RightSidebar({
  activeTab,
  onTabChange,
  sceneIndex,
  avatarName,
  avatarImageUrl,
  voiceName,
  onSelectAvatar,
  onSelectVoice,
  onRenderScene,
  isRendering,
  canRender,
  persons,
  voices,
  libraryAvatars,
  selectedLibraryImageId,
  selectedSceneType,
  onLayoutChange,
  captionSettings,
  onUpdateCaptionSettings,
}: IRightSidebarProps) {
  const handleClosePanel = () => {
    // Toggle off by switching to the same tab (no-op for now)
  };

  return (
    <div className="flex h-full w-[24.5%] flex-shrink-0">
      {/* Panel content area */}
      <div className="flex-1 border-l border-border">
        {activeTab === 'avatar-voice' && (
          <AvatarVoicePanel
            sceneIndex={sceneIndex}
            avatarName={avatarName}
            avatarImageUrl={avatarImageUrl}
            voiceName={voiceName}
            onSelectAvatar={onSelectAvatar}
            onSelectVoice={onSelectVoice}
            onRenderScene={onRenderScene}
            isRendering={isRendering}
            canRender={canRender}
            onClose={handleClosePanel}
            persons={persons}
            voices={voices}
            libraryAvatars={libraryAvatars}
            selectedLibraryImageId={selectedLibraryImageId}
          />
        )}
        {activeTab === 'layouts' && (
          <LayoutsPanel
            onClose={handleClosePanel}
            selectedSceneType={selectedSceneType}
            onLayoutChange={onLayoutChange}
          />
        )}
        {activeTab === 'captions' && (
          <CaptionsPanel
            onClose={handleClosePanel}
            captionSettings={captionSettings}
            onUpdateCaptionSettings={onUpdateCaptionSettings}
          />
        )}
      </div>

      {/* Icon menu bar */}
      <div className="flex w-16 flex-col items-center gap-1 border-l border-border bg-background-secondary pt-2">
        <IconMenuButton
          icon={User}
          tab="avatar-voice"
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
        <IconMenuButton
          icon={Captions}
          tab="captions"
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
        <IconMenuButton
          icon={LayoutGrid}
          tab="layouts"
          activeTab={activeTab}
          onTabChange={onTabChange}
        />
      </div>
    </div>
  );
}

export { RightSidebar };
